/**
 * Produces a deterministic JSON string for any JSON-compatible value.
 *
 * Guarantees:
 * - Object keys are sorted alphabetically at every nesting level.
 * - Keys whose values are `undefined` are omitted.
 * - Keys listed in `ignoreKeys` are omitted.
 * - Primitive values (string, number, boolean, null) are serialized with
 *   standard `JSON.stringify` semantics.
 * - Arrays preserve their element order.
 *
 * This ensures that two structurally equivalent values always produce
 * the same string regardless of original key insertion order.
 *
 * @param value      - The value to serialize.
 * @param ignoreKeys - Optional set of key names to strip at every level.
 * @returns A deterministic JSON string.
 *
 * @internal Not exported from the package — use `hashNode` instead.
 */
export function canonicalize(value: unknown, ignoreKeys?: Set<string>): string {
	return serializeValue(value, ignoreKeys);
}

function serializeValue(value: unknown, ignoreKeys: Set<string> | undefined): string {
	// null
	if (value === null) {
		return 'null';
	}

	// undefined — should not normally appear in JSON, but handle gracefully
	if (value === undefined) {
		return 'null';
	}

	// Primitives
	switch (typeof value) {
		case 'string':
			return JSON.stringify(value);
		case 'number':
			// Handle special numeric values that JSON.stringify would emit as null
			if (!Number.isFinite(value)) {
				return 'null';
			}
			return String(value);
		case 'boolean':
			return value ? 'true' : 'false';
	}

	// Arrays
	if (Array.isArray(value)) {
		const items = value.map((item) => serializeValue(item, ignoreKeys));
		return '[' + items.join(',') + ']';
	}

	// Objects
	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;
		const keys = Object.keys(obj)
			.filter((key) => {
				if (obj[key] === undefined) {
					return false;
				}
				if (ignoreKeys && ignoreKeys.has(key)) {
					return false;
				}
				return true;
			})
			.sort();

		const entries = keys.map(
			(key) => JSON.stringify(key) + ':' + serializeValue(obj[key], ignoreKeys),
		);

		return '{' + entries.join(',') + '}';
	}

	// Fallback for unexpected types (symbol, function, bigint, etc.)
	return 'null';
}
